import {Component, Input, OnInit} from '@angular/core';
import {DbActiveModal} from "../../../../../modules/modal/modal-ref";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../../store';
import {filter, first, map, switchMap, takeUntil, tap} from "rxjs/operators";
import {ActivityAction} from "../../../../../activities/activity";
import {Subject} from "rxjs";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {BoosterService} from "../../services/booster.service";
import {combineLatest} from "rxjs";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {PerformanceService} from "../../../performance/performance.service";
import {UserSubscription} from "../../../../../user/user-subscription";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {DefineStep} from "../../../../../activities/define-booster-optimization-progress-step";
import {Utils} from "../../../../../shared/utils/utils";
import {Website} from "../../../website";
import {DbModalService} from "../../../../../modules/modal/modal";
import {User} from "../../../../../user/user";
import {UserReferral} from "../../../../../user/user-referral";
import {getUserLocalStorage, getUserLocalStorageLoaded} from "../../../../../store";

type Step = 'progress' | 'apiUnavailable' | 'planNotSupported' | 'success';

interface IScoreMeta {
  mobile_score: number;
  desktop_score: number;
  mobile_tti: number;
  desktop_tti: number;
}

export interface IBoosterScore {
  score: number;
  type: 'mobile' | 'desktop';
  time: number;
}

@Component({
    selector: 'db-optimization-dialog',
    templateUrl: './optimization-dialog.component.html',
    styleUrls: ['./optimization-dialog.component.scss'],
    standalone: false
})
export class OptimizationDialogComponent implements OnInit {

  @Input() websiteId: number;
  @Input() userSubscription?: UserSubscription;
  @Input() user?: User;
  @Input() apiUnavailable: boolean = false;
  @Input() success?: boolean = false;
  public planNotSupported: boolean = false;
  public website: Website;
  public optimizaionId: any;
  public scores: IBoosterScore[] = [];

  public step: Step = 'progress';
  public cannotEnableCache: boolean;
  public postOptChecked: boolean = false;
  public flowFinished: boolean = false;
  public userRefferalSubsciption: any;
  public stepsCount: number = 0;
  public optimizationSteps: string[];

  private _unOptimizedScoreMeta: IScoreMeta;
  private _optimizedScoreMeta: IScoreMeta;
  private _screenShotGenerated: boolean;
  private _optimizationTimer: any;
  private _optimizationInterrupt$: Subject<boolean> = new Subject<boolean>();
  private _userLocalStorage: any;

  constructor(
    private _activeModal: DbActiveModal,
    private _store: Store<fromStore.DbStoreState>,
    private _boosterService: BoosterService,
    private _performanceService: PerformanceService,
    private _modalService: DbModalService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    ) { }

  ngOnInit() {
    this._addEventHandlers();
    this._getUserSubscription();
    this.websiteId && this._getOptimizationId();

    /* after 30 seconds check where is flow and notifs are not coming*/
    setTimeout(() => {
      this.websiteId && !this.flowFinished && this._getOptimizationId();
    }, 30000);

    /* after 3 minutes check where is flow stucked */
    setTimeout(() => {
      this.websiteId && this._getOptimizationId();
    }, 180000);
    /*TODO fix this part*/
    this._store.select(fromStore.getUserReferralSubscriptionLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserReferralSubscription)),
      )
      .subscribe((userReferralSubscription: UserReferral) => {
        this.userRefferalSubsciption = userReferralSubscription;
      });
  }

  private _getUserSubscription(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserSubscription)),
        first()
      )
      .subscribe((userSubscription: UserSubscription) => {
        this.userSubscription = userSubscription;
        this.planNotSupported = this.userSubscription && this.userSubscription.plan && this.userSubscription.plan.services && !this.userSubscription.plan.services.speed_optimizer;
        this.step = this.planNotSupported ? 'planNotSupported' : this.apiUnavailable ? 'apiUnavailable' : 'progress';
        this.apiUnavailable && this._sendGAEvent('Connection Fail');
        this._activeModal.hideCloseBtn(this.step === 'progress');
      });
  }

  private _getOngoingOptimizingActivities() {
    this._store.select(fromStore.getOngoingActivities)
      .pipe(takeUntil(this._optimizationInterrupt$))
      .subscribe(ongoingActivities => {
        const activity = ongoingActivities.find(({action}) => action === ActivityAction.OPTIMIZING_FIRST_PAGE);
        if (activity) {
          const steps = activity.meta && activity.meta.steps ? activity.meta.steps : [];
          if (Utils.arrayContains(steps, DefineStep.connectionFlowFinish)) {
            this._store.dispatch( new fromStore.LoadWebsites() );
            this._getScoresMeta(activity, true);
          }
        }
      });
  }

  private _getOptimizationId(): void {
    !!this.websiteId && this._performanceService.getConnectionFlowProgress(this.websiteId)
      .pipe(
        first(response => !!response),
      )
      .subscribe((response: DbResponse): void => {
        if (response.success) {
          this.optimizaionId = response.data && response.data.id ? response.data.id : null;
          const steps = response.data && response.data.steps ? response.data.steps : [];
          if (Utils.arrayContains(steps, DefineStep.connectionFlowFinish)) {
            this.flowFinished = true;
            this.step = 'success';
            !this.postOptChecked && this._getPostOptimizationScore();
          }
        }
      });
  }

  private _getPostOptimizationScore(): void {
    this.postOptChecked = true;
    TypeUtils.isDefined(this.optimizaionId) && this._performanceService.postOptimizationTasks(this.websiteId, this.optimizaionId);
  }

  private _addEventHandlers(): void {
    this._getOngoingOptimizingActivities();
    this._getOngoingActivity();
  }

  private _getOngoingActivity(): void {
    this._store.select(fromStore.getOngoingActivity)
      .pipe(
        tap(a => console.log(a)),
        takeUntil(this._optimizationInterrupt$),
        filter(activity => activity && activity.websiteId === this.websiteId)
      )
      .subscribe(activity => {
        if (activity) {
          this._getScoresMeta(activity);
        }
      });
  }

  private _getScoresMeta(activity, success = false): void {
    const _notOptimizedMetaDebugScore = activity && activity.meta && activity.meta.debugData && activity.meta.debugData.not_optimized_score_check_finish && activity.meta.debugData.not_optimized_score_check_finish.current_score ? activity.meta.debugData.not_optimized_score_check_finish.current_score : this._unOptimizedScoreMeta;
    const _optimizedMetaDebugScore = activity && activity.meta && activity.meta.debugData && activity.meta.debugData.optimized_score_check_finish && activity.meta.debugData.optimized_score_check_finish.current_score ? activity.meta.debugData.optimized_score_check_finish.current_score : this._optimizedScoreMeta;
    this._screenShotGenerated = activity.action === ActivityAction.GENERATE_WEBSITE_SCREENSHOT ? true : this._screenShotGenerated;
    this._unOptimizedScoreMeta = activity.meta && activity.meta.check_with_no_optimization && activity.meta.current_score ? activity.meta.current_score : _notOptimizedMetaDebugScore;
    this._optimizedScoreMeta = activity.meta && !activity.meta.check_with_no_optimization && activity.meta.current_score ? activity.meta.current_score : _optimizedMetaDebugScore;
    if (this._unOptimizedScoreMeta && this._optimizedScoreMeta) {
      if (activity.meta && activity.meta.steps && Utils.arrayContains(activity.meta.steps, DefineStep.connectionFlowFinish)) {
        this._showSuccess();
      }
    }
    else if (success) {
      this._showSuccess();
    }
    (this._unOptimizedScoreMeta && this._optimizedScoreMeta && this._screenShotGenerated)
      ? this._showOptimizationSuccess() : this._handleOptimizationProcess();
  }

  public resize(size: [number, number]): void {
    this._activeModal.resize(...size);
  }

  public close(): void {
    this._activeModal.close();
  }

  private _handleOptimizationProcess() {
    if (this._optimizationTimer) clearTimeout(this._optimizationTimer);
    this._optimizationTimer = setTimeout(() => {
      if (this._optimizedScoreMeta) {
        this._showOptimizationSuccess();
      } else {
        this._showSuccess();
        this._handleOptimizationProcess();
      }
    }, 240_000);
  }

  private _showSuccess(): void {
    this._store.select(getUserLocalStorageLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => this._store.select(getUserLocalStorage)),
        first(),
      )
      .subscribe(userLocalStorage => {
        this._userLocalStorage = userLocalStorage;
      });
    /*this._store.dispatch(new fromStore.LoadUserReferralSubscription());*/
    !this.postOptChecked && this._getPostOptimizationScore();
    this.step = 'success';
  }

  private _showOptimizationSuccess(): void {
    this._getCacheStatus();
  }

  private _getCacheStatus(): void {
    this._store.select(fromStore.getWebsite(this.websiteId))
      .pipe(
        first(website => !!website),
        map(website => website.site_url),
        switchMap(url => combineLatest(
          this._boosterService.getOptimizeStatus(this.websiteId, url),
          this._boosterService.getClearCacheStatus(this.websiteId),
        )),
        first(([optimizeStatus, clearCacheStatus]) => !!optimizeStatus && !!clearCacheStatus)
      )
      .subscribe(([optimizeStatusResponse, clearCacheStatusResponse]) => {
        this._getCacheInfo(optimizeStatusResponse, clearCacheStatusResponse);
        if (this._optimizationTimer) clearTimeout(this._optimizationTimer);
        this._optimizationInterrupt$.next(null);
        this._optimizationInterrupt$.complete();
        this._showSuccess();
        this._activeModal.hideCloseBtn(false);
        this._activeModal.setBackdrop(true);
      });
  }

  private _getCacheInfo(optimizeStatusResponse, clearCacheStatusResponse): void {
    let cachingStatus = {pageIsCached: false, pageIsOptimized: false, cachingDate: 0};
    let cacheEnabled = false;
    let clearCachingDate = 0;
    if (optimizeStatusResponse.success) {
      cachingStatus = {
        pageIsCached: !!optimizeStatusResponse.data.page_is_cached,
        pageIsOptimized: !!optimizeStatusResponse.data.page_is_optimized,
        cachingDate: parseInt(optimizeStatusResponse.data.cache_clear_date, 10),
      };
    }
    if (clearCacheStatusResponse.success) {
      cacheEnabled = !!clearCacheStatusResponse.data.page_cache;
      clearCachingDate = +clearCacheStatusResponse.data.clear_cache_date;
      this._store.dispatch(new fromStore.SetClearCacheStatus(
        {websiteId: this.websiteId, cacheEnabled: cacheEnabled, cacheDate: clearCacheStatusResponse.data.clear_cache_date}
      ));
    }
    this.cannotEnableCache = clearCachingDate && cachingStatus.cachingDate && clearCachingDate !== cachingStatus.cachingDate;
    this.cannotEnableCache && this._sendGAEvent('Clear Cache Fail');
  }

  private _sendGAEvent(action): void {
    const analyticsData = {
      eventCategory: 'Dashboard Error',
      eventAction: action,
      eventLabel: 'Speed Booster'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }
}
